import Router from 'vue-router';
import NProgress from 'nprogress';
import noop from '@tencent/tks-utils/function/noop';
import { setDocumentTitle, domTitle } from '@/utils/domUtil';
import get from 'lodash/get';
import overEvery from 'lodash/overEvery';
import 'nprogress/nprogress.css';

export class TksRouter {
  /**
   * TksRouter 权限路由
   * @param {Object} options
   * @param {Promise} options.getAuth
   * @param {Array} options.routers
   */
  constructor(options) {
    // eslint-disable-next-line no-multi-assign
    this.options = options = {
      /**
       * 路由实例
       */
      routerOptions: {},
      /**
       * @Boolean
       * 进度条，为 false 则不显示进度条
       */
      progress: true,
      /**
       * 自动选择 meta 中的 title 更新到浏览器的title上
       */
      autoTitle: true,
      /**
       * 基础路由，应不涉及权限，eg: 登录注册页面、404等
       */
      basicRoutes: [],
      /**
       * 动态路由，根据权限匹配
       */
      routeFilters: [TksRouterUtils.roleFilter],
      routeValidates: [TksRouterUtils.routeNeedAuth],
      dynamicRoutes: [],
      onBeforeEach: noop,
      onAfterEach: noop,
      onRouterAuthed: noop,
      /**
       * @Promise
       * 权限接口
       */
      fetchState: null,
      ...options,
    };
    if (!options.fetchState) {
      throw new Error('arguments error');
    }
    this.routerInstance = new Router(this.options.routerOptions);
    this._authed = false;
    this.roles = [];
    this.init();
  }

  init() {
    const { basicRoutes, onBeforeEach, onAfterEach } = this.options;
    const { routerInstance } = this;
    // 添加基础路由
    window.tksRouter = this;
    routerInstance.addRoutes(basicRoutes);
    routerInstance.beforeEach(async (to, from, next) => {
      // call hooks
      await onBeforeEach(to, from, next);

      this.progress(true);
      this.updateTitle(to);

      if (!this._authed) {
        const needValidate = overEvery(this.options.routeValidates);
        // 用户还没有登录，还没有权限
        if (!needValidate(this.state, to) && to.matched.length) {
          // 访问基础路由，不需要登录
          next();
        } else {
          // 加载用户权限，生成动态路由
          await this.prepareDynamicRoutes(to, from, next);
        }
        return;
      }
      // 已经登录，有角色了
      next();
    });
    routerInstance.afterEach((...args) => {
      this.progress(false);
      // call hooks
      onAfterEach(args);
    });
  }

  updateTitle(to) {
    const title = to.meta && to.meta.title;
    title && setDocumentTitle(`${to.meta.title} - ${domTitle}`);
  }

  async prepareDynamicRoutes(to, from, next) {
    if (this._authed) {
      return;
    }
    const { fetchState } = this.options;
    const { routerInstance } = this;
    let redirect;
    const redirectNext = (to) => {
      redirect = to;
    };

    this.state = await fetchState(redirectNext);

    if (redirect) {
      const matched = routerInstance.match(redirect, routerInstance.currentRoute);
      if (from.path === matched.path) {
        // hack 路由到相同页面解决vue报错问题
        next({
          ...redirect,
          query: {
            t: Date.now(),
            ...redirect.query,
          },
        });
      } else {
        next(redirect);
      }
      return;
    }
    this._authed = true;
    const { onRouterAuthed, dynamicRoutes, routeFilters } = this.options;
    const filterFun = overEvery(routeFilters);
    const routes = this.matchRoutes(dynamicRoutes, filterFun);

    routerInstance.addRoutes(routes);
    onRouterAuthed(routes);

    redirect = decodeURIComponent(from.query.redirect || to.path);
    if (to.path === redirect) {
      // hack: true so the navigation will not leave a history record
      next({ ...to, replace: true });
    } else {
      // 跳转到目的路由
      next({ path: redirect });
    }
  }

  matchRoutes(routes, filterFun) {
    return routes.filter((route) => {
      if (filterFun(this.state, route)) {
        const len = route.children && route.children.length;
        const matchRoutes = this.matchRoutes.bind(this);
        if (len) {
          route.children = matchRoutes(route.children, filterFun);
          route.redirect = route.children[0].path;
        }
        return true;
      }
      return false;
    });
  }

  /**
   * 进度条
   * @param {Boolean|Error} status
   */
  progress(status) {
    if (!this.options.progress) {
      return;
    }
    if (typeof status === 'object' && status.message) {
      // 出错
    } else {
      status ? NProgress.start() : NProgress.done();
    }
  }
}

TksRouter.Router = Router;

export const TksRouterUtils = {
  roleFilter(state, route) {
    const requiredRoles = TksRouterUtils.routeNeedAuth(state, route);
    if (!requiredRoles) {
      // 该路由不需要鉴权
      return true;
    }
    return requiredRoles.find((role) => state.roles.includes(role));
  },
  routeNeedAuth(state, route) {
    const roles = get(route, 'meta.roles', []);
    return roles.length ? roles : false;
  },
};

export default {
  TksRouter,
  TksRouterUtils,
};
